import { erwaehnungSchreiben } from '@netcon-work/shared'
import { useEffect, useRef, useState } from 'react'
import { dateienAus } from '../lib/anhang'
import { Markdown } from '../lib/markdown'

/**
 * **Textfeld mit Auszeichnung** (Nachtrag zu Phase 4, 03.08.2026).
 *
 * Nils: *„da sollten wir noch auch Markdown oder so als Formatierer anbieten oder bisschen
 * WYSIWYG."*
 *
 * ## Warum Markdown mit Knöpfen und kein echtes WYSIWYG
 *
 * Ein WYSIWYG-Feld führt einen eigenen Dokumentenzustand und gibt am Ende HTML aus. Das
 * hieße: eine große Abhängigkeit, ein Sanitizer beim Anzeigen – und vor allem ein
 * **zweites Format** neben dem, das die Quellsysteme sprechen. GitLab-Kommentare sind
 * Markdown; ein Kommentar, den Work zurückschreibt, muss dort ankommen, wie er hier
 * aussah.
 *
 * Die Knöpfe holen das Wesentliche vom WYSIWYG zurück: **man muss die Zeichen nicht
 * kennen.** Wer sie kennt, tippt sie weiter; wer nicht, markiert und klickt. Und die
 * Vorschau beantwortet die einzige Frage, die ein WYSIWYG sonst beantwortet – *wie sieht
 * das nachher aus?*
 */

interface Werkzeug {
  label: string
  titel: string
  /** Was vor und hinter die Markierung kommt. */
  huelle: [string, string]
  /** Wirkt auf die ganze Zeile statt auf die Markierung (Listen, Zitate). */
  zeilenweise?: boolean
  taste?: string
}

const WERKZEUGE: Werkzeug[] = [
  { label: 'F', titel: 'Fett (⌘B)', huelle: ['**', '**'], taste: 'b' },
  { label: 'K', titel: 'Kursiv (⌘I)', huelle: ['*', '*'], taste: 'i' },
  { label: '‹›', titel: 'Code', huelle: ['`', '`'] },
  { label: '•', titel: 'Liste', huelle: ['- ', ''], zeilenweise: true },
  { label: '1.', titel: 'Nummerierte Liste', huelle: ['1. ', ''], zeilenweise: true },
  { label: '❝', titel: 'Zitat', huelle: ['> ', ''], zeilenweise: true },
  { label: '🔗', titel: 'Link', huelle: ['[', '](https://)'] },
]

export interface ErwaehnbarePerson {
  id: string
  displayName: string
  /** Blasser Zusatz hinter dem Namen, z. B. „per Mail" für Leute ohne Work-Konto. */
  hinweis?: string
}

/**
 * **Was gerade als Erwähnung getippt wird** – der Text zwischen einem `@` und dem Zeiger.
 *
 * Drei Bedingungen, und jede hat einen Grund:
 *
 * - Vor dem `@` steht Zeilenanfang oder ein Leerzeichen. Sonst fängt `nils@wani.ng`
 *   mitten in einer Adresse eine Personensuche an.
 * - Kein `[`, `]`, `(`, `)` im Bruchstück: das sind die Zeichen einer fertigen
 *   Erwähnung, und in einer fertigen sucht man nicht weiter.
 * - Höchstens 30 Zeichen und keine Zeilenschaltung – ein Name, kein Absatz.
 */
const ANGEFANGENE_ERWAEHNUNG = /(?:^|\s)@([^@[\]()\n]{0,30})$/

/** Höhe der Auswahlliste – auch die Grundlage der Entscheidung „nach oben oder unten". */
const LISTE_HOCH = 188

/**
 * Passt der getippte Anfang auf diesen Namen?
 *
 * Verglichen wird **wortweise von vorn**: „wa" findet „Nils Waning", „nils wa" auch, „ils"
 * nicht. Eine Suche mitten im Wort träfe bei 30 Personen zu oft; wer tippt, meint den
 * Anfang eines der Namensteile.
 */
function passt(name: string, bruchstueck: string): boolean {
  const gesucht = bruchstueck.trim().toLowerCase()
  if (!gesucht) return true
  const teile = name.toLowerCase().split(/\s+/)
  return gesucht.split(/\s+/).every((wort) => teile.some((teil) => teil.startsWith(wort)))
}

export function MarkdownFeld({
  wert,
  setzen,
  platzhalter,
  zeilen = 3,
  beiAbsenden,
  beiAbbrechen,
  beiDateien,
  laedtHoch = false,
  erwaehnbar,
  fokusBeimEinhaengen = false,
}: {
  wert: string
  setzen: (neu: string) => void
  platzhalter?: string
  zeilen?: number
  /** ⌘↵ sendet ab, ohne zur Maus zu greifen (Leitplanke 3). */
  beiAbsenden?: () => void
  /**
   * **Esc legt das Formular weg – aber nur, solange nichts drinsteht** (01.10.2026).
   *
   * Wer keinen übergibt, bekommt das Feld wie vorher: Esc schließt dann nur die @-Auswahl.
   * Siehe die Begründung an der Tastenbehandlung.
   */
  beiAbbrechen?: () => void
  /**
   * **Bilder und Dateien direkt ins Feld** ([E-313](../../../../docs/ENTSCHEIDUNGEN.md#e-313),
   * 02.09.2026).
   *
   * Aus Ticket NCW-6X848M: *„Inline-Bilder in Beschreibungsfeld."*
   *
   * Am Kommentarfeld gab es das längst – Ablegen, Einfügen, Knopf –, nur hing es am
   * **Formular** darum herum und nicht am Feld. Damit war es an das Kommentarformular gebunden
   * und stand der Beschreibung nicht zur Verfügung. Jetzt kann es jedes `MarkdownFeld`, das
   * einen Empfänger mitbringt; wer keinen übergibt, bekommt das Feld wie vorher.
   *
   * Der Aufrufer entscheidet, was aus der Datei wird – hochladen und den Verweis einsetzen tut
   * er, weil nur er weiß, an welcher Aufgabe oder welchem Vorhaben sie hängt.
   */
  beiDateien?: (dateien: File[]) => void
  /** Solange etwas hochlädt: der Knopf sagt es, statt so zu tun, als sei nichts passiert. */
  laedtHoch?: boolean
  /**
   * **Wer mit `@` angesprochen werden kann** (Nils, 18.08.2026).
   *
   * Fehlt die Liste, gibt es die Auswahl nicht – ein Beschreibungsfeld ist keine
   * Unterhaltung, und ein Melde-Formular kennt das Team gar nicht.
   */
  erwaehnbar?: ErwaehnbarePerson[]
  /**
   * **Beim Erscheinen den Fokus holen und ins Bild rücken**
   * ([E-329](../../../../docs/ENTSCHEIDUNGEN.md#e-329)).
   *
   * Gebraucht, wenn das Kommentarformular unter einen Kommentar springt: es steht dann an
   * einer anderen Stelle der Seite, und wer eben noch „antworten" geklickt hat, tippt sonst
   * ins Leere. Nur einmal beim Einhängen – ein Feld, das sich den Fokus immer wieder holt,
   * wäre eines, aus dem man nicht mehr herauskommt.
   */
  fokusBeimEinhaengen?: boolean
}) {
  const feld = useRef<HTMLTextAreaElement>(null)
  const [vorschau, setVorschau] = useState(false)
  /** Was gerade gesucht wird – `null` heißt: die Auswahl ist zu. */
  const [suche, setSuche] = useState<string | null>(null)
  const [markiert, setMarkiert] = useState(0)
  /**
   * **Klappt die Liste nach oben?** (Nils, 20.08.2026, mit Bildschirmfoto: *„Bei @ in den
   * Kommentaren rutscht die Dropdown aus dem Bildschirm."*)
   *
   * Das Kommentarfeld steht im Aufgabendetail ganz unten; eine Liste, die immer nach unten
   * aufgeht, liegt dort außerhalb des Fensters. Entschieden wird beim Öffnen, an der
   * tatsächlichen Lage des Feldes – nicht an einer Vermutung darüber, wo es steht.
   */
  const [nachOben, setNachOben] = useState(false)

  /**
   * **Einmal beim Einhängen, nie wieder.** Die leere Abhängigkeitsliste ist hier die Aussage,
   * nicht die Nachlässigkeit: springt das Formular an eine andere Stelle, ist es für React ein
   * neues Feld – der Effekt läuft genau dann, wenn er soll.
   *
   * `block: 'nearest'` scrollt nur, wenn wirklich nötig, und dann so wenig wie möglich. Ein
   * `'center'` risse die Seite bei jedem Klick auf „antworten" in die Mitte, auch wenn der
   * Kommentar längst zu sehen war.
   */
  useEffect(() => {
    if (!fokusBeimEinhaengen) return
    feld.current?.focus({ preventScroll: true })
    feld.current?.scrollIntoView({ block: 'nearest' })
    /** Bewusst nur beim Einhängen – die Regel `react-hooks/exhaustive-deps` ist hier nicht konfiguriert. */
  }, [])

  const treffer =
    suche === null || !erwaehnbar
      ? []
      : erwaehnbar.filter((p) => passt(p.displayName, suche)).slice(0, 8)
  const auswahlOffen = treffer.length > 0

  /** Nach jeder Eingabe: steht der Zeiger hinter einem `@…`? */
  function sucheNachfuehren(text: string, zeiger: number) {
    if (!erwaehnbar?.length) return
    const bruchstueck = ANGEFANGENE_ERWAEHNUNG.exec(text.slice(0, zeiger))?.[1]
    setSuche(bruchstueck ?? null)
    setMarkiert(0)

    if (bruchstueck !== undefined) {
      const kasten = feld.current?.getBoundingClientRect()
      /** Reicht der Platz unter dem Feld nicht für die Liste, klappt sie nach oben. */
      if (kasten) setNachOben(window.innerHeight - kasten.bottom < LISTE_HOCH + 16)
    }
  }

  /**
   * Die Auswahl übernehmen: das getippte `@…` wird durch die vollständige Erwähnung
   * ersetzt, und dahinter kommt ein Leerzeichen – sonst klebt das nächste Wort am Namen.
   */
  /**
   * **Text einsetzen, ohne den Rückgängig-Verlauf wegzuwerfen**
   * ([E-310](../../../../docs/ENTSCHEIDUNGEN.md#e-310), 02.09.2026).
   *
   * Aus Ticket NCW-6X848M: *„Strg+Z in Beschreibungsfeld klappt nicht."*
   *
   * Die Ursache war jede Zeile, die den Text über `setzen(…)` **neu setzte**: fett, Liste,
   * Erwähnung. Für React ist das ein neuer Wert von außen, für den Browser ein Feld, dessen
   * Inhalt sich ohne sein Zutun geändert hat – und damit ist sein Rückgängig-Stapel wertlos und
   * wird verworfen. Danach greift Strg+Z ins Leere, und zwar **für alles, was davor getippt
   * wurde**, nicht nur für den Knopfdruck.
   *
   * `execCommand('insertText')` ist dafür das richtige Werkzeug, obwohl es als „veraltet"
   * gilt: es fügt so ein, **als hätte der Mensch getippt**. Der Browser schreibt den Schritt in
   * seinen Stapel, feuert ein `input`-Ereignis, und React bekommt seinen Wert über dasselbe
   * `onChange` wie beim Tippen. Es gibt bis heute keinen Ersatz dafür – die vorgeschlagene
   * Nachfolge-Schnittstelle ist in keinem Browser umgesetzt.
   *
   * **Der Rückfall bleibt.** In jsdom (also in den Tests) gibt es `execCommand` nicht, und ein
   * Browser darf `false` liefern. Dann wird gesetzt wie vorher: lieber ein verlorener
   * Rückgängig-Schritt als ein Knopf, der nichts tut.
   */
  function einfuegen(
    von: number,
    bis: number,
    text: string,
    /** Wo der Zeiger danach steht – oder was danach **markiert** ist. */
    zeigerNach: number | readonly [number, number],
  ) {
    const el = feld.current
    if (!el) return

    el.focus()
    el.setSelectionRange(von, bis)

    const eingefuegt =
      typeof document.execCommand === 'function' &&
      (() => {
        try {
          return document.execCommand('insertText', false, text)
        } catch {
          return false
        }
      })()

    if (!eingefuegt) setzen(wert.slice(0, von) + text + wert.slice(bis))

    window.requestAnimationFrame(() => {
      el.focus()
      const [a, b] = typeof zeigerNach === 'number' ? [zeigerNach, zeigerNach] : zeigerNach
      el.setSelectionRange(a, b)
    })
  }

  function erwaehnungEinsetzen(person: ErwaehnbarePerson) {
    const el = feld.current
    if (!el) return
    const zeiger = el.selectionStart
    const davor = wert.slice(0, zeiger)
    const angefangen = ANGEFANGENE_ERWAEHNUNG.exec(davor)
    if (!angefangen) return
    const beginn = davor.length - (angefangen[1]?.length ?? 0) - 1
    const eingesetzt = `${erwaehnungSchreiben(person)} `
    setSuche(null)
    einfuegen(beginn, zeiger, eingesetzt, beginn + eingesetzt.length)
  }

  function anwenden(werkzeug: Werkzeug) {
    const el = feld.current
    if (!el) return
    const { selectionStart: von, selectionEnd: bis } = el
    const [vorne, hinten] = werkzeug.huelle

    if (werkzeug.zeilenweise) {
      /**
       * Bei Listen zählt die **Zeile**, nicht die Markierung: wer drei Zeilen markiert und
       * auf „Liste" klickt, will drei Punkte – nicht einen Bindestrich vor dem Block.
       */
      const zeilenAnfang = wert.lastIndexOf('\n', von - 1) + 1
      const zeilenEnde = wert.indexOf('\n', bis) === -1 ? wert.length : wert.indexOf('\n', bis)
      const betroffen = wert.slice(zeilenAnfang, zeilenEnde).split('\n')
      const nummeriert = vorne.startsWith('1.')
      const neu = betroffen
        .map((z, n) => (nummeriert ? `${n + 1}. ${z}` : `${vorne}${z}`))
        .join('\n')
      einfuegen(zeilenAnfang, zeilenEnde, neu, zeilenAnfang + neu.length)
      return
    }

    const markiert = wert.slice(von, bis)
    einfuegen(
      von,
      bis,
      vorne + markiert + hinten,
      /**
       * Was markiert war, bleibt markiert – nur um die Hülle verschoben. Ohne Markierung
       * landet der Zeiger zwischen den Zeichen, dort tippt man weiter.
       */
      markiert ? ([von + vorne.length, bis + vorne.length] as const) : von + vorne.length,
    )
  }

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-center gap-0.5">
        {WERKZEUGE.map((w) => (
          <button
            key={w.label}
            type="button"
            onClick={() => anwenden(w)}
            title={w.titel}
            aria-label={w.titel}
            disabled={vorschau}
            className="cursor-pointer rounded-md px-1.5 py-0.5 text-[11.5px] text-ink2 hover:bg-[#f3f2ef] disabled:cursor-default disabled:opacity-40"
          >
            {w.label}
          </button>
        ))}
        {/**
         * **Der Knopf ist ein Etikett vor einem versteckten `<input type="file">`** – ein
         * Dateiauswahl-Dialog lässt sich nur aus einer echten Anwenderaktion heraus öffnen,
         * und ein `<input>` lässt sich nicht so gestalten, dass es zu den übrigen Knöpfen passt.
         * Dieselbe Bauform wie am Kommentarfeld seit dem 24.08.2026.
         */}
        {beiDateien && (
          <label
            className="cursor-pointer rounded-md px-1.5 py-0.5 text-[11.5px] text-ink2 hover:bg-[#f3f2ef]"
            title="Bild oder Datei anhängen – geht auch mit Ziehen und Strg+V"
          >
            {laedtHoch ? '…' : '📎'}
            <input
              type="file"
              multiple
              className="hidden"
              disabled={vorschau || laedtHoch}
              onChange={(e) => {
                const dateien = [...(e.target.files ?? [])]
                /** Zurücksetzen, sonst meldet dieselbe Datei beim zweiten Mal kein `change`. */
                e.target.value = ''
                if (dateien.length > 0) beiDateien(dateien)
              }}
            />
          </label>
        )}
        {/** Foto direkt aus der Kamera – nur auf dem Telefon (Z-16), siehe `AufgabeDetail`. */}
        {beiDateien && (
          <label
            className="cursor-pointer rounded-md px-1.5 py-0.5 text-[11.5px] text-ink2 hover:bg-[#f3f2ef] sm:hidden"
            title="Foto aufnehmen"
          >
            📷
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              disabled={vorschau || laedtHoch}
              onChange={(e) => {
                const dateien = [...(e.target.files ?? [])]
                e.target.value = ''
                if (dateien.length > 0) beiDateien(dateien)
              }}
            />
          </label>
        )}

        <button
          type="button"
          onClick={() => setVorschau((v) => !v)}
          className="ml-auto cursor-pointer rounded-md px-1.5 py-0.5 text-[11.5px] text-ink2 hover:bg-[#f3f2ef]"
        >
          {vorschau ? 'Bearbeiten' : 'Vorschau'}
        </button>
      </div>

      {vorschau ? (
        <div
          className="min-h-[64px] rounded-[9px] border border-line2 bg-[#fafaf8] px-[11px] py-2 text-[12.5px]"
          aria-label="Vorschau"
        >
          {wert.trim() ? (
            <Markdown text={wert} kompakt />
          ) : (
            <span className="text-muted">Noch nichts geschrieben.</span>
          )}
        </div>
      ) : (
        <div className="relative">
          <textarea
            ref={feld}
            value={wert}
            onChange={(e) => {
              setzen(e.target.value)
              sucheNachfuehren(e.target.value, e.target.selectionStart)
            }}
            /** Der Zeiger kann auch ohne Tippen wandern – Klick, Pfeiltaste, Auswahl. */
            onSelect={(e) => sucheNachfuehren(wert, e.currentTarget.selectionStart)}
            onBlur={() => setSuche(null)}
            /**
             * **Ablegen und Einfügen am Feld selbst** (E-313).
             *
             * `onDragOver` mit `preventDefault` ist Pflicht: ohne ihn ist das Feld kein
             * gültiges Ziel, und `onDrop` liefe nie. Der Screenshot aus der Zwischenablage
             * (⌘⇧4) ist der wichtigere der beiden Wege – er kommt über `onPaste`.
             *
             * **Ohne Dateien nicht eingreifen**: wer Text einfügt, soll ihn eingefügt bekommen.
             *
             * ## Warum `stopPropagation` und nicht nur `preventDefault`
             *
             * Davin, 28.09.2026: Seine beiden Kommentare trugen dasselbe Bild **zweimal** –
             * `![](anhang:…)` mit derselben Kennung, bei nur **einem** Anhang in der Ablage.
             *
             * Über dem Feld liegt ein zweiter Handler am Formular (`AufgabeDetail.tsx`), der es
             * für den Fall gibt, dass jemand **neben** das Feld einfügt. Beim Einfügen **im** Feld
             * liefen beide: erst der hier, dann derselbe noch einmal beim Aufstieg. Die Datei ging
             * zweimal hinauf, der Server erkannte sie am SHA-256 wieder und gab **dieselbe**
             * Kennung zurück – und der Entwurf bekam den Verweis doppelt.
             *
             * `preventDefault` allein half nicht: es unterdrückt nur das Einfügen durch den
             * Browser, nicht den Aufstieg. Wer hier zugegriffen hat, hat die Sache erledigt.
             */
            onDragOver={beiDateien ? (e) => e.preventDefault() : undefined}
            onDrop={
              beiDateien
                ? (e) => {
                    const dateien = dateienAus(e.dataTransfer)
                    if (dateien.length === 0) return
                    e.preventDefault()
                    e.stopPropagation()
                    beiDateien(dateien)
                  }
                : undefined
            }
            onPaste={
              beiDateien
                ? (e) => {
                    const dateien = dateienAus(e.clipboardData)
                    if (dateien.length === 0) return
                    e.preventDefault()
                    e.stopPropagation()
                    beiDateien(dateien)
                  }
                : undefined
            }
            onKeyDown={(e) => {
              /**
               * **Die Auswahl hat Vorrang vor allem anderen** (Leitplanke 3): solange sie
               * offen ist, gehören ↑ ↓ ↵ ⇥ ihr. Sonst schickte das ↵, das den Namen
               * bestätigen soll, den halben Kommentar ab.
               */
              if (auswahlOffen) {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault()
                  setMarkiert(
                    (n) => (n + (e.key === 'ArrowDown' ? 1 : treffer.length - 1)) % treffer.length,
                  )
                  return
                }
                if (e.key === 'Enter' || e.key === 'Tab') {
                  const gewaehlt = treffer[markiert]
                  if (gewaehlt) {
                    e.preventDefault()
                    erwaehnungEinsetzen(gewaehlt)
                    return
                  }
                }
                if (e.key === 'Escape') {
                  e.preventDefault()
                  /**
                   * **`stopPropagation`, sonst schließt dasselbe Esc das Aufgabenfenster**
                   * (01.10.2026).
                   *
                   * `AufgabeKontext` hängt einen Zuhörer ans `window` („Esc schließt"), und
                   * `preventDefault` hält den nicht auf – es unterdrückt nur die Vorgabe des
                   * Browsers, nicht den Aufstieg. Wer die @-Auswahl mit Esc wegklickte, verlor
                   * damit das ganze Fenster **und den Entwurf**.
                   */
                  e.stopPropagation()
                  setSuche(null)
                  return
                }
              }
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && beiAbsenden) {
                e.preventDefault()
                beiAbsenden()
                return
              }
              /**
               * **Esc im leeren Feld legt das Formular weg** (01.10.2026, Christian Hunhoff:
               * *„das schließen von Kommentaren, wenn man eigentlich nichts ändern will oder
               * hinzufügen will"*).
               *
               * **Nur im leeren Feld**, und das ist der ganze Punkt: Der Fall, den Christian
               * meint, ist „ich habe «antworten» geklickt und will doch nicht" – da steht
               * nichts drin. Stünde ein halber Kommentar da, wäre Esc eine Taste, die
               * zehn Minuten Arbeit wegwirft, ohne zu fragen; dafür gibt es den sichtbaren
               * Knopf daneben.
               *
               * `stopPropagation` aus demselben Grund wie oben – das Fenster soll hier
               * zubleiben, geschlossen wird das Formular.
               */
              if (e.key === 'Escape' && beiAbbrechen && wert.trim() === '') {
                e.preventDefault()
                e.stopPropagation()
                beiAbbrechen()
                return
              }
              const werkzeug = WERKZEUGE.find((w) => w.taste && w.taste === e.key.toLowerCase())
              if (werkzeug && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                anwenden(werkzeug)
              }
            }}
            rows={zeilen}
            placeholder={platzhalter}
            role={auswahlOffen ? 'combobox' : undefined}
            aria-expanded={auswahlOffen}
            aria-controls={auswahlOffen ? 'erwaehnung-auswahl' : undefined}
            className="max-sm:text-[16px] w-full resize-y rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[12.5px]"
          />

          {auswahlOffen && (
            <ul
              id="erwaehnung-auswahl"
              role="listbox"
              aria-label="Person erwähnen"
              className={`absolute z-20 w-[280px] overflow-y-auto rounded-[9px] border border-line2 bg-surface py-1 shadow-lg ${
                nachOben ? 'bottom-full mb-1' : 'mt-0.5'
              }`}
              style={{ maxHeight: LISTE_HOCH }}
            >
              {treffer.map((p, n) => (
                <li key={p.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={n === markiert}
                    /**
                     * `onMouseDown` statt `onClick`: der Klick käme erst nach dem
                     * `blur` des Textfelds, und das schließt die Auswahl vorher.
                     */
                    onMouseDown={(e) => {
                      e.preventDefault()
                      erwaehnungEinsetzen(p)
                    }}
                    onMouseEnter={() => setMarkiert(n)}
                    className={`flex w-full cursor-pointer items-baseline gap-2 px-2.5 py-1 text-left text-[12.5px] ${
                      n === markiert ? 'bg-[#f3f2ef]' : ''
                    }`}
                  >
                    <span className="truncate">{p.displayName}</span>
                    {/**
                     * **Wer kein Work-Konto hat, bekommt nur eine Mail** – und das gehört an
                     * die Stelle, an der man ihn auswählt. Sonst wundert man sich später,
                     * warum jemand nicht reagiert, der die Aufgabe gar nicht öffnen kann.
                     */}
                    {p.hinweis && (
                      <span className="ml-auto shrink-0 text-[10.5px] text-muted">{p.hinweis}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
